import type { ReactNode } from "react";
import { Eye, Search, Telescope, Compass, ClipboardList, Lightbulb, Microscope, ScanSearch, FileSearch, Radar, BookOpen, Sparkles, Zap, Cpu, Bot, Wand2, Workflow, Rocket, Atom, CircuitBoard, Sparkle, Play, Cog, Handshake, Users, Route, LifeBuoy, HeartHandshake, MessagesSquare, UserCheck, Milestone, Network, Boxes, Blocks, Share2, Waypoints, Puzzle, Cable, Combine, Spline, Unplug, Hexagon, Plug, Brain, Database, Layers, BrainCircuit, Gem, Server, HardDrive, Shield, ShieldCheck, Lock, BadgeCheck, KeyRound, FileCheck, Fingerprint, ShieldAlert, Gauge, Target, LineChart, TrendingUp, Award, Flag, Star, Activity, Settings, Wrench, GitBranch, Globe, Building2, Briefcase, PenTool } from "lucide-react";
import { FONT, T, WARM, PETROL, SAGE, CREAMc } from "../theme";
import type { IconCat } from "../types";

// ICON LIBRARY — clean light Lucide (shadcn) icons for the site, grouped by our sections (Erez 2026-07-10).
export const ICON_LIB: { group: string; icons: [string, React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>][] }[] = [
  { group: "Understand / discovery", icons: [["Eye", Eye], ["Search", Search], ["Telescope", Telescope], ["Compass", Compass], ["ClipboardList", ClipboardList], ["Lightbulb", Lightbulb], ["Microscope", Microscope], ["ScanSearch", ScanSearch], ["FileSearch", FileSearch], ["Radar", Radar], ["BookOpen", BookOpen]] },
  { group: "Deploy / bring AI", icons: [["Sparkles", Sparkles], ["Zap", Zap], ["Cpu", Cpu], ["Bot", Bot], ["Wand2", Wand2], ["Workflow", Workflow], ["Rocket", Rocket], ["Atom", Atom], ["CircuitBoard", CircuitBoard], ["Sparkle", Sparkle], ["Play", Play], ["Cog", Cog]] },
  { group: "Partner", icons: [["Handshake", Handshake], ["Users", Users], ["Route", Route], ["LifeBuoy", LifeBuoy], ["HeartHandshake", HeartHandshake], ["MessagesSquare", MessagesSquare], ["UserCheck", UserCheck], ["Milestone", Milestone]] },
  { group: "Connect / platform", icons: [["Network", Network], ["Boxes", Boxes], ["Blocks", Blocks], ["Share2", Share2], ["Waypoints", Waypoints], ["Puzzle", Puzzle], ["Cable", Cable], ["Combine", Combine], ["Spline", Spline], ["Unplug", Unplug], ["Hexagon", Hexagon], ["Plug", Plug]] },
  { group: "Intelligence / data", icons: [["Brain", Brain], ["Database", Database], ["Layers", Layers], ["BrainCircuit", BrainCircuit], ["Gem", Gem], ["Server", Server], ["HardDrive", HardDrive]] },
  { group: "Secure / governed", icons: [["Shield", Shield], ["ShieldCheck", ShieldCheck], ["Lock", Lock], ["BadgeCheck", BadgeCheck], ["KeyRound", KeyRound], ["FileCheck", FileCheck], ["Fingerprint", Fingerprint], ["ShieldAlert", ShieldAlert]] },
  { group: "Growth / outcomes", icons: [["Gauge", Gauge], ["Target", Target], ["LineChart", LineChart], ["TrendingUp", TrendingUp], ["Award", Award], ["Flag", Flag], ["Star", Star], ["Activity", Activity]] },
  { group: "General / tools", icons: [["Settings", Settings], ["Wrench", Wrench], ["GitBranch", GitBranch], ["Globe", Globe], ["Building2", Building2], ["Briefcase", Briefcase], ["PenTool", PenTool]] },
];

export const LOGO_IMG = "https://tailormind-site.pages.dev/board/logo";

export function Mark({ els, size }: { els: ReactNode; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      {els}
    </svg>
  );
}

export const ICON_CATS: IconCat[] = [
  {
    id: "stem", title: "★ Single-stem trunk", note: "Instead of three branches, ONE clean trunk that flares open and connects into the canopy. du1 canopy, redesigned trunk. (newest)",
    items: [
      { id: "s-1", label: "st1 · flare", img: `${LOGO_IMG}/line/st1.svg` },
      { id: "s-2", label: "st2 · shallow Y", img: `${LOGO_IMG}/line/st2.svg` },
      { id: "s-3", label: "st3 · vase", img: `${LOGO_IMG}/line/st3.svg` },
      { id: "s-4", label: "st4 · curved", img: `${LOGO_IMG}/line/st4.svg` },
      { id: "s-5", label: "st5 · trumpet", img: `${LOGO_IMG}/line/st5.svg` },
      { id: "s-6", label: "st6 · minimal", img: `${LOGO_IMG}/line/st6.svg` },
    ],
  },
  {
    id: "weight", title: "★ Thickness ladder (numbered)", note: "du4 & du1, thicker in numbered steps. weight N = pixels grown each side @1024 source (higher = thicker). LIVE now on BOTH site versions: du4 · weight 4. Icon also shrunk to 26px. (newest)",
    items: [
      { id: "w-d4-8", label: "du4 · weight 8", img: `${LOGO_IMG}/line/du4-w8.svg` },
      { id: "w-d4-14", label: "du4 · weight 14 · LIVE", img: `${LOGO_IMG}/line/du4-w14.svg` },
      { id: "w-d4-20", label: "du4 · weight 20", img: `${LOGO_IMG}/line/du4-w20.svg` },
      { id: "w-d1-8", label: "du1 · weight 8", img: `${LOGO_IMG}/line/du1-w8.svg` },
      { id: "w-d1-14", label: "du1 · weight 14", img: `${LOGO_IMG}/line/du1-w14.svg` },
      { id: "w-d1-20", label: "du1 · weight 20", img: `${LOGO_IMG}/line/du1-w20.svg` },
    ],
  },
  {
    id: "thick", title: "Thickness ladder — du1 & du4 (t1-3, thinner)", note: "Earlier ladder — superseded by the numbered weights above.",
    items: [
      { id: "t-d4-1", label: "du4 · thin", img: `${LOGO_IMG}/line/du4-t1.svg` },
      { id: "t-d4-2", label: "du4 · medium", img: `${LOGO_IMG}/line/du4-t2.svg` },
      { id: "t-d4-3", label: "du4 · thick", img: `${LOGO_IMG}/line/du4-t3.svg` },
      { id: "t-d1-1", label: "du1 · thin", img: `${LOGO_IMG}/line/du1-t1.svg` },
      { id: "t-d1-2", label: "du1 · medium", img: `${LOGO_IMG}/line/du1-t2.svg` },
      { id: "t-d1-3", label: "du1 · thick", img: `${LOGO_IMG}/line/du1-t3.svg` },
    ],
  },
  {
    id: "duna", title: "★ Duna-like — minimal, fewer lines", note: "Closer to duna: our tree shape, far FEWER lines. Was live: v1 = du4, v2 = du2.",
    items: [
      { id: "d-1", label: "du1 · silhouette only", img: `${LOGO_IMG}/line/du1.svg` },
      { id: "d-2", label: "du2 · LIVE v2", img: `${LOGO_IMG}/line/du2.svg` },
      { id: "d-3", label: "du3 · 3 lines", img: `${LOGO_IMG}/line/du3.svg` },
      { id: "d-4", label: "du4 · LIVE v1", img: `${LOGO_IMG}/line/du4.svg` },
      { id: "d-5", label: "du5 · symmetric", img: `${LOGO_IMG}/line/du5.svg` },
      { id: "d-6", label: "du6 · crystal cuts", img: `${LOGO_IMG}/line/du6.svg` },
    ],
  },
  {
    id: "geo", title: "★ Line — geometric inside, bolder", note: "Kept the OUTER silhouette; INTERNAL lines more geometric + thicker. Was live: v1 = gm3, v2 = gm2.",
    items: [
      { id: "g-1", label: "gm1 · triangulated", img: `${LOGO_IMG}/line/gm1.svg` },
      { id: "g-2", label: "gm2 · LIVE v2", img: `${LOGO_IMG}/line/gm2.svg` },
      { id: "g-3", label: "gm3 · LIVE v1", img: `${LOGO_IMG}/line/gm3.svg` },
      { id: "g-4", label: "gm4 · diamonds", img: `${LOGO_IMG}/line/gm4.svg` },
      { id: "g-5", label: "gm5 · minimal straight", img: `${LOGO_IMG}/line/gm5.svg` },
      { id: "g-6", label: "gm6 · crystal", img: `${LOGO_IMG}/line/gm6.svg` },
    ],
  },
  {
    id: "line2", title: "★ Line — fewer lines (gradient)", note: "Same faceted shape, internal lines stripped gradually (many → none). Was live: v1 = ff3, v2 = ff4.",
    items: [
      { id: "f-1", label: "ff1 · many", img: `${LOGO_IMG}/line/ff1.svg` },
      { id: "f-2", label: "ff2", img: `${LOGO_IMG}/line/ff2.svg` },
      { id: "f-3", label: "ff3 · LIVE v1", img: `${LOGO_IMG}/line/ff3.svg` },
      { id: "f-4", label: "ff4 · LIVE v2", img: `${LOGO_IMG}/line/ff4.svg` },
      { id: "f-5", label: "ff5", img: `${LOGO_IMG}/line/ff5.svg` },
      { id: "f-6", label: "ff6 · silhouette", img: `${LOGO_IMG}/line/ff6.svg` },
    ],
  },
  {
    id: "line", title: "★ Line icons — vectors", note: "The v2 faceted tree as a flat BLACK LINE icon — outer silhouette + internal facet lines, smooth & vectorized (true SVG).",
    items: [
      { id: "l-1", label: "Full facets", img: `${LOGO_IMG}/line/ln1.svg` },
      { id: "l-2", label: "Simplified", img: `${LOGO_IMG}/line/ln2.svg` },
      { id: "l-3", label: "Bold", img: `${LOGO_IMG}/line/ln3.svg` },
      { id: "l-4", label: "Thin", img: `${LOGO_IMG}/line/ln4.svg` },
      { id: "l-5", label: "Smoothed", img: `${LOGO_IMG}/line/ln5.svg` },
      { id: "l-6", label: "Crisp geometric", img: `${LOGO_IMG}/line/ln6.svg` },
    ],
  },
  {
    id: "blue", title: "★ Dark blue — faceted", note: "The faceted tree in monochrome DARK BLUE — brand colour, but a true blue that works next to black & white.",
    items: [
      { id: "bl-deep", label: "1 · deep navy", img: `${LOGO_IMG}/blue-deep.png` },
      { id: "bl-navy", label: "2 · navy", img: `${LOGO_IMG}/blue-navy.png` },
      { id: "bl-mid", label: "3 · blue", img: `${LOGO_IMG}/blue-mid.png` },
      { id: "bl-steel", label: "4 · steel", img: `${LOGO_IMG}/blue-steel.png` },
    ],
  },
  {
    id: "grade", title: "★ Greyscale grade — dark → current", note: "The original faceted shape, same silhouette, graded from near-black to the current tone.",
    items: [
      { id: "gr-1", label: "1 · darkest", img: `${LOGO_IMG}/poly-grade-1.png` },
      { id: "gr-2", label: "2", img: `${LOGO_IMG}/poly-grade-2.png` },
      { id: "gr-3", label: "3 · mid", img: `${LOGO_IMG}/poly-grade-3.png` },
      { id: "gr-4", label: "4", img: `${LOGO_IMG}/poly-grade-4.png` },
      { id: "gr-5", label: "5 · current", img: `${LOGO_IMG}/poly-grade-5.png` },
    ],
  },
  {
    id: "poly", title: "★ Polygon / low-poly", note: "The old tailormind.co faceted tree — kept, and reworked into the new black & white.",
    items: [
      { id: "y-bw-tree", label: "Faceted tree", img: `${LOGO_IMG}/poly-bw-tree.jpg` },
      { id: "y-bw-round", label: "Round facets", img: `${LOGO_IMG}/poly-bw-round.jpg` },
      { id: "y-bw-tall", label: "Tall facets", img: `${LOGO_IMG}/poly-bw-tall.jpg` },
      { id: "y-bw-leaf", label: "Faceted leaf", img: `${LOGO_IMG}/poly-bw-leaf.jpg` },
      { id: "y-gray", label: "Original, greyscale", img: `${LOGO_IMG}/poly-gray.png` },
      { id: "y-orig", label: "Original (petrol) — reference", img: `${LOGO_IMG}/poly-orig.png` },
    ],
  },
  {
    id: "black", title: "★ Black — ink & silhouette", note: "The painterly beauty, in black & white — high contrast, real presence.",
    items: [
      { id: "k-silhouette", label: "Silhouette", img: `${LOGO_IMG}/bk-silhouette.jpg` },
      { id: "k-sumi", label: "Sumi brush", img: `${LOGO_IMG}/bk-sumi-tree.jpg` },
      { id: "k-brain", label: "Brain-tree", img: `${LOGO_IMG}/bk-brain-tree.jpg` },
      { id: "k-round", label: "Round canopy", img: `${LOGO_IMG}/bk-round-canopy.jpg` },
      { id: "k-lino", label: "Linocut", img: `${LOGO_IMG}/bk-lino-tree.jpg` },
      { id: "k-branch", label: "Brush branch", img: `${LOGO_IMG}/bk-brush-branch.jpg` },
      { id: "k-leaf", label: "Ink leaf", img: `${LOGO_IMG}/bk-sumi-leaf.jpg` },
      { id: "k-sprout", label: "Ink sprout", img: `${LOGO_IMG}/bk-ink-sprout.jpg` },
    ],
  },
  {
    id: "bold", title: "1 · Bold / filled", note: "Solid weight, real presence — the opposite of thin lines.",
    items: [
      { id: "b-tree", label: "Tree", els: (<><circle cx="16" cy="12" r="8.5" fill="currentColor" stroke="none" /><rect x="14.3" y="18" width="3.4" height="11" rx="1.4" fill="currentColor" stroke="none" /></>) },
      { id: "b-leaf", label: "Leaf", els: (<path d="M25 5C13 5 6 12 6 24c12 0 19-7 19-19z" fill="currentColor" stroke="none" />) },
      { id: "b-pine", label: "Pine", els: (<><path d="M16 3L24 13H8zM16 9L25 21H7z" fill="currentColor" stroke="none" /><rect x="14.3" y="21" width="3.4" height="8" rx="1" fill="currentColor" stroke="none" /></>) },
      { id: "b-blob", label: "Canopy", els: (<><path d="M16 4c-4 0-7 2-8 5-3 0-5 3-4 6 1 3 3 4 6 4h12c3 0 5-1 6-4 1-3-1-6-4-6-1-3-4-5-8-5z" fill="currentColor" stroke="none" /><rect x="14.3" y="19" width="3.4" height="10" rx="1.2" fill="currentColor" stroke="none" /></>) },
      { id: "b-sprout", label: "Sprout", els: (<><rect x="15" y="14" width="2" height="15" rx="1" fill="currentColor" stroke="none" /><path d="M15.5 17C15.5 12 11.5 9 6.5 9c0 5 4 8 9 8z" fill="currentColor" stroke="none" /><path d="M16.5 14C16.5 9 20.5 6 25.5 6c0 5-4 8-9 8z" fill="currentColor" stroke="none" /></>) },
    ],
  },
  {
    id: "duo", title: "2 · Duotone", note: "Ink + a warm/petrol accent — a bit of colour and depth.",
    items: [
      { id: "d-tree", label: "Tree", els: (<><circle cx="16" cy="12" r="8.5" fill={WARM} stroke="none" /><rect x="14.3" y="18" width="3.4" height="11" rx="1.4" fill="currentColor" stroke="none" /></>) },
      { id: "d-leaf", label: "Leaf", els: (<><path d="M25 5C13 5 6 12 6 24c12 0 19-7 19-19z" fill={SAGE} stroke="none" /><path d="M10 20L21 9" stroke="#1C1917" strokeWidth="1.6" fill="none" strokeLinecap="round" /></>) },
      { id: "d-pine", label: "Pine", els: (<><path d="M16 3L24 13H8zM16 9L25 21H7z" fill={PETROL} stroke="none" /><rect x="14.3" y="21" width="3.4" height="8" rx="1" fill="currentColor" stroke="none" /></>) },
      { id: "d-canopy", label: "Foliage", els: (<><circle cx="11" cy="13" r="6" fill={WARM} stroke="none" /><circle cx="21" cy="13" r="6" fill={SAGE} stroke="none" /><circle cx="16" cy="8.5" r="6.2" fill={PETROL} stroke="none" /><rect x="14.5" y="18" width="3" height="11" rx="1.2" fill="currentColor" stroke="none" /></>) },
    ],
  },
  {
    id: "round", title: "3 · Rounded / organic", note: "Thick rounded strokes — friendly, softer character.",
    items: [
      { id: "r-tree", label: "Tree", els: (<><circle cx="16" cy="12" r="7.5" strokeWidth="2.6" /><path d="M16 19.5V29" strokeWidth="2.6" /></>) },
      { id: "r-leaf", label: "Leaf", els: (<><path d="M24 6C13 6 7 13 7 23c10 0 17-7 17-17z" strokeWidth="2.4" /><path d="M11 19L20 10" strokeWidth="2.2" /></>) },
      { id: "r-sprout", label: "Sprout", els: (<><path d="M16 29V15" strokeWidth="2.6" /><path d="M16 17C16 12 12 9 7 9" strokeWidth="2.6" /><path d="M16 14C16 9 20 6 25 6" strokeWidth="2.6" /></>) },
      { id: "r-branch", label: "Branch", els: (<><path d="M16 29V17" strokeWidth="2.6" /><path d="M16 17L10 11" strokeWidth="2.6" /><path d="M16 17L22 11" strokeWidth="2.6" /></>) },
    ],
  },
  {
    id: "badge", title: "4 · Badge / emblem", note: "Mark inside a tile — app-icon / avatar feel, strong at any size.",
    items: [
      { id: "g-ink", label: "Ink tile", els: (<><rect x="2" y="2" width="28" height="28" rx="8" fill="#1C1917" stroke="none" /><circle cx="16" cy="14" r="6" fill={CREAMc} stroke="none" /><rect x="14.9" y="19" width="2.2" height="7" rx="1" fill={CREAMc} stroke="none" /></>) },
      { id: "g-petrol", label: "Petrol tile", els: (<><rect x="2" y="2" width="28" height="28" rx="8" fill={PETROL} stroke="none" /><path d="M24 8C14 8 9 13 9 23c10 0 15-5 15-15z" fill={CREAMc} stroke="none" /></>) },
      { id: "g-outline", label: "Outline tile", els: (<><rect x="2.5" y="2.5" width="27" height="27" rx="8" fill={CREAMc} stroke="#1C1917" strokeWidth="1.6" /><circle cx="16" cy="14" r="6" fill="#1C1917" stroke="none" /><rect x="14.9" y="19" width="2.2" height="7" rx="1" fill="#1C1917" stroke="none" /></>) },
      { id: "g-circle", label: "Circle badge", els: (<><circle cx="16" cy="16" r="14" fill="#1C1917" stroke="none" /><path d="M22 10C14 10 10 14 10 22c8 0 12-4 12-12z" fill={CREAMc} stroke="none" /></>) },
    ],
  },
  {
    id: "paint", title: "5 · Painterly (Nano Banana)", note: "Warm gouache emblems in the hero's own language — richest, most owned.",
    items: [
      { id: "p-tree", label: "Tree", img: `${LOGO_IMG}/nb-tree.jpg` },
      { id: "p-canopy", label: "Canopy", img: `${LOGO_IMG}/nb-canopy.jpg` },
      { id: "p-leaf", label: "Leaf", img: `${LOGO_IMG}/nb-leaf.jpg` },
      { id: "p-sprout", label: "Sprout", img: `${LOGO_IMG}/nb-sprout.jpg` },
    ],
  },
];


export function LogoCategoryNode({ data }: { data: IconCat & { bust: string } }) {
  return (
    <div style={{ fontFamily: FONT, width: 660, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 26 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
        <span style={{ fontSize: 21, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{data.title}</span>
      </div>
      <div style={{ fontSize: 13, color: T.ink2, marginBottom: 20 }}>{data.note}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
        {data.items.map((m) => (
          <div key={m.id} style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: "18px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, background: T.bg }}>
            {m.img
              ? <img src={`${m.img}?t=${data.bust}`} alt={m.label} draggable={false} style={{ width: 60, height: 60, objectFit: "contain", borderRadius: 10 }} />
              : <div style={{ color: T.ink }}><Mark els={m.els} size={54} /></div>}
            <div style={{ display: "flex", alignItems: "center", gap: 7, color: T.ink }}>
              {m.img
                ? <img src={`${m.img}?t=${data.bust}`} alt="" draggable={false} style={{ width: 20, height: 20, objectFit: "contain", borderRadius: 4 }} />
                : <Mark els={m.els} size={20} />}
              <span style={{ fontSize: 15, fontWeight: 500, letterSpacing: "0.01em" }}>Tailormind</span>
            </div>
            {m.label && <span style={{ fontSize: 11, color: T.ink2 }}>{m.label}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function IconGalleryNode({ data }: { data: { groups: typeof ICON_LIB } }) {
  return (
    <div style={{ fontFamily: FONT, width: 1420, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.12)", padding: 30 }}>
      <div style={{ fontSize: 21, fontWeight: 800, color: T.ink }}>Icon library — Lucide (shadcn)</div>
      <div style={{ fontSize: 13, color: T.ink2, marginTop: 4, marginBottom: 8 }}>Clean, light thin-line icons — closer to Duna. Tell me which you like per section and I'll swap them onto the site.</div>
      {data.groups.map((g) => (
        <div key={g.group} style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#2B5560", marginBottom: 12 }}>{g.group}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 12 }}>
            {g.icons.map(([name, Icon]) => (
              <div key={name} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 9, padding: "18px 8px", background: T.bg, border: `1px solid ${T.line}`, borderRadius: 12 }}>
                <Icon size={32} strokeWidth={1.4} color={T.ink} />
                <span style={{ fontSize: 11, color: T.ink2, textAlign: "center" }}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

